<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    /** Convenience boolean (legacy): true → 'lg' padding, false → 'none'. */
    padded?: boolean;
    /** Explicit padding scale; wins over `padded` when set. */
    padding?: 'sm' | 'md' | 'lg' | 'none';
    /** shadow = card border + quiet shadow (default), border = the card border only,
     * flat = neither, tile = the wash inner tile that sits INSIDE a card (spec §6:
     * one container below the panel, one card above it, never tone on tone). */
    variant?: 'shadow' | 'border' | 'flat' | 'tile';
    /** `label` renders the title as the uppercase 11 px section label of a detail
     * card (spec §8, the Person page); `title` is the bold card title. */
    heading?: 'title' | 'label';
    dashed?: boolean;
    interactive?: boolean;
    /** A narrow card lets the header's actions drop under the heading instead of crushing it. */
    wrapHead?: boolean;
    /**
     * Fill the host's height (a dashboard panel in a fixed-height slot): the header stays
     * put (and wraps, as `wrapHead`), and whatever follows it — a list, or its loading /
     * empty / error state — takes the rest and scrolls inside.
     */
    fill?: boolean;
  }>(),
  { padded: true, dashed: false, interactive: false, variant: 'shadow', heading: 'title', wrapHead: false, fill: false },
);

const pad = computed(() => props.padding ?? (props.padded ? 'lg' : 'none'));
</script>

<template>
  <div
    class="lumi-card"
    :class="[
      `lumi-card--${variant}`,
      `lumi-card--pad-${pad}`,
      { 'lumi-card--dashed': dashed, 'lumi-card--interactive': interactive, 'lumi-card--head-wrap': wrapHead || fill, 'lumi-card--fill': fill },
    ]"
  >
    <header v-if="$slots.header || $slots.actions || title || subtitle" class="lumi-card__head">
      <div class="lumi-card__heading">
        <h3 v-if="title" class="lumi-card__title" :class="{ 'lumi-card__title--label': heading === 'label' }">{{ title }}</h3>
        <p v-if="subtitle" class="lumi-card__subtitle">{{ subtitle }}</p>
        <slot name="header" />
      </div>
      <div v-if="$slots.actions" class="lumi-card__actions"><slot name="actions" /></div>
    </header>
    <slot />
    <footer v-if="$slots.footer" class="lumi-card__foot"><slot name="footer" /></footer>
  </div>
</template>

<style>
.lumi-card{background:var(--surface-card);border:1px solid var(--border-default);border-radius:var(--lumi-card-radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);}
.lumi-card--border{box-shadow:none;}
.lumi-card--flat{border-color:transparent;box-shadow:none;}
.lumi-card--tile{background:var(--surface-wash);border-color:transparent;border-radius:var(--lumi-card-tile-radius);box-shadow:none;}
.lumi-card--pad-none{padding:0;}
.lumi-card--pad-sm{padding:var(--space-3);}
.lumi-card--pad-md{padding:var(--space-4);}
.lumi-card--pad-lg{padding:var(--space-5);}
.lumi-card--dashed{border-style:dashed;border-color:var(--panel-edge);box-shadow:none;background:transparent;}
.lumi-card--interactive{cursor:pointer;transition:border-color var(--dur-fast),box-shadow var(--dur-fast),background-color var(--dur-fast);}
.lumi-card--interactive:hover{border-color:var(--panel-edge);box-shadow:var(--shadow-sm);}
.lumi-card--tile.lumi-card--interactive:hover{background:var(--accent-soft-2);box-shadow:none;}
/* Instance variables (defaults in tokens/spacing.css), set on the card like
   `--lumi-table-max-h` on a table: `--lumi-card-head-gap` (the space under the header),
   `--lumi-card-heading-flex` (the heading's flex, e.g. `1 1 22rem` so it claims room before
   the actions wrap), `--lumi-card-radius` and `--lumi-card-tile-radius`. */
.lumi-card__head{display:flex;align-items:flex-start;justify-content:space-between;gap:var(--space-3);margin-bottom:var(--lumi-card-head-gap);}
.lumi-card--head-wrap > .lumi-card__head{flex-wrap:wrap;row-gap:var(--space-2);}
/* Wrapped to a line of their own, the actions get the card's width and no more —
   wider content (a run of seat pips, say) wraps inside them instead of pushing
   the card, and the page with it, out past a phone's edge. */
.lumi-card--head-wrap > .lumi-card__head > .lumi-card__actions{max-width:100%;min-width:0;}
.lumi-card--fill{height:100%;min-height:0;display:flex;flex-direction:column;}
.lumi-card--fill > .lumi-card__head{flex-shrink:0;}
.lumi-card--fill > :not(.lumi-card__head):not(.lumi-card__foot){flex:1;min-height:0;overflow-y:auto;}
.lumi-card__heading{min-width:0;flex:var(--lumi-card-heading-flex);}
.lumi-card__title{font-size:var(--text-base);font-weight:var(--weight-bold);letter-spacing:var(--tracking-display);color:var(--text-heading);margin:0;}
.lumi-card__title--label{font-size:var(--text-caps);font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--text-tertiary);}
.lumi-card__subtitle{font-size:var(--text-sm);color:var(--text-secondary);margin:2px 0 0;}
.lumi-card__actions{display:flex;align-items:center;gap:var(--space-2);flex-shrink:0;}
.lumi-card__foot{margin-top:var(--space-4);padding-top:var(--space-4);border-top:1px solid var(--border-default);display:flex;justify-content:flex-end;gap:var(--space-2);}
</style>
